import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin, DollarSign, Clock } from "lucide-react";
import api, { imageUrl } from "../lib/api";
import Seo from "../components/Seo";
import BeforeAfterSlider from "../components/BeforeAfterSlider";
import CTASection from "../components/home/CTASection";
import usePublicData from "../hooks/usePublicData";
import { TESTIMONIALS } from "../mock";
import { Star } from "lucide-react";
import useReveal from "../hooks/useReveal";

export default function ProjectDetail() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const { data: reviews } = usePublicData("/reviews", TESTIMONIALS);
  useReveal([project]);

  useEffect(() => {
    api.get(`/projects/${slug}`).then((res) => setProject(res.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="pt-40 pb-32 text-center">
        <h1 className="font-display text-3xl font-bold text-[#0f2740] mb-4">Project not found</h1>
        <Link to="/projects" className="text-[#2f9fe6] font-semibold">Back to projects</Link>
      </div>
    );
  }
  if (!project) return <div className="pt-40 pb-40 text-center text-[#6b7683]">Loading...</div>;

  const cover = imageUrl(project.cover) || (project.images[0] ? imageUrl(project.images[0]) : "");
  const gallery = (project.images || []).map(imageUrl);
  const beforeAfter = (project.before_after || []).filter((b) => b.before && b.after);

  return (
    <>
      <Seo title={project.title} path={`/projects/${project.slug}`} description={project.description || `${project.title} — a renovation project by Classic Interior Renovations in Edmonton.`} image={cover} />

      <section className="relative pt-40 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          {cover && <img src={cover} alt={project.title} className="w-full h-full object-cover" />}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0f2740]/95 via-[#0f2740]/82 to-[#0f2740]/45" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0f2740]/70 to-transparent" />
        </div>
        <div className="container-x relative z-10">
          <Link to="/projects" className="inline-flex items-center gap-2 text-[#a9c6dd] hover:text-white mb-6 transition-colors"><ArrowLeft className="w-4 h-4" /> All projects</Link>
          {project.tag && <span className="inline-block px-3 py-1 rounded-full bg-[#2f9fe6] text-white text-xs font-semibold mb-4 capitalize">{project.tag || project.category}</span>}
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-white leading-tight max-w-3xl">{project.title}</h1>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container-x">
          <div className="grid lg:grid-cols-3 gap-10 mb-14">
            <div className="lg:col-span-2 reveal">
              <h2 className="font-display text-2xl font-bold text-[#0f2740] mb-4">About this project</h2>
              <p className="text-[#6b7683] text-lg leading-relaxed whitespace-pre-line">{project.description || "A beautifully executed renovation by our team."}</p>
            </div>
            <div className="reveal-right bg-[#f7f9fb] rounded-2xl p-7 border border-[#eef1f4] h-fit space-y-5">
              <div className="flex items-center gap-3"><MapPin className="w-5 h-5 text-[#2f9fe6]" /><div><div className="text-xs text-[#9aa4af] uppercase">Location</div><div className="font-semibold text-[#0f2740]">{project.location || "Edmonton"}</div></div></div>
              <div className="flex items-center gap-3"><DollarSign className="w-5 h-5 text-[#2f9fe6]" /><div><div className="text-xs text-[#9aa4af] uppercase">Budget</div><div className="font-semibold text-[#0f2740]">{project.budget || "On request"}</div></div></div>
              <div className="flex items-center gap-3"><Clock className="w-5 h-5 text-[#2f9fe6]" /><div><div className="text-xs text-[#9aa4af] uppercase">Timeline</div><div className="font-semibold text-[#0f2740]">{project.length || "—"}</div></div></div>
            </div>
          </div>

          {beforeAfter.length > 0 && (
            <div className="mb-14">
              <h2 className="font-display text-2xl font-bold text-[#0f2740] mb-6 reveal">Before &amp; After</h2>
              <div className="grid md:grid-cols-2 gap-8">
                {beforeAfter.map((b, i) => (
                  <div key={b.before || i} className="reveal"><BeforeAfterSlider before={imageUrl(b.before)} after={imageUrl(b.after)} label={b.label} /></div>
                ))}
              </div>
            </div>
          )}

          {gallery.length > 0 && (
            <div>
              <h2 className="font-display text-2xl font-bold text-[#0f2740] mb-6 reveal">Gallery</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {gallery.map((g, i) => (
                  <button key={g || i} onClick={() => setLightbox(g)} className={`reveal delay-${(i % 3) + 1} shape-soft group`}>
                    <img src={g} alt={`${project.title} ${i + 1}`} className="w-full h-56 object-cover transition-transform duration-500 group-hover:scale-105" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {lightbox && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-6" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="Project" className="max-w-full max-h-full rounded-xl" />
        </div>
      )}

      {/* REVIEWS */}
      {reviews && reviews.length > 0 && (
        <section className="py-24 bg-[#0f2740]">
          <div className="container-x">
            <div className="text-center max-w-2xl mx-auto mb-14 reveal">
              <div className="flex items-center justify-center gap-3 mb-4">
                <span className="h-px w-10 bg-[#2f9fe6]" /><span className="text-[#7fb8de] tracking-[0.24em] text-xs font-semibold uppercase">Reviews</span><span className="h-px w-10 bg-[#2f9fe6]" />
              </div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">What our clients say</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {reviews.slice(0, 6).map((r, i) => (
                <div key={r.id || r.name} className={`reveal delay-${(i % 3) + 1} bg-white/5 rounded-2xl p-7 border border-white/10`} data-testid={`project-review-${i}`}>
                  <div className="flex mb-3">{[...Array(r.rating || 5)].map((_, s) => <Star key={s} className="w-4 h-4 fill-[#2f9fe6] text-[#2f9fe6]" />)}</div>
                  <p className="text-[#dbe7f1] text-sm leading-relaxed mb-4">“{r.text}”</p>
                  <div className="text-white font-semibold text-sm">{r.name}</div>
                  <div className="text-[#7fb8de] text-xs">{r.role}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <CTASection />
    </>
  );
}
